import * as mqtt from 'async-mqtt'
import { AsyncMqttClient } from 'async-mqtt'
import {
  MqttValue,
  MqttContainer,
  MqttSubscription,
  MqttMessage,
  mqttValueConversion,
  instanceOfMqttValue,
} from '../mqttDefinitions'
import { defineStore } from 'pinia'
import { MqttTopic } from '@/mqttDefinitions/topics'
import discoverService from '@/service/discoverService'

export const useMqttStore = defineStore('mqttStore', {
  state: () => ({
    connection: {
      protocol: 'ws',
      host: 'bikefitting',
      // ws: 8083; wss: 8084
      port: 9001,
      endpoint: '/mqtt',
      // for more options, please refer to https://github.com/mqttjs/MQTT.js#mqttclientstreambuilder-options
      clean: true,
      connectTimeout: 4000, // ms
      reconnectPeriod: 1000, // ms
      rejectUnauthorized: false,
      qos: 1,
    },

    client: {} as AsyncMqttClient,

    connected: false,
    connecting: false,
    connectionError: '',

    retryTimes: 0,

    messages: [] as MqttMessage[],
  }),

  getters: {
    getMessageOnTopic: (state) => (definition: MqttValue) => {
      const returnValue = state.messages.find(
        (message) => message.topic == definition.topic,
      )?.payload
      return mqttValueConversion(
        returnValue,
        definition.conversionValue,
        'receive',
      )
    },

    getRawValueOnTopic: (state) => (topic: string) => {
      const returnValue = state.messages.find(
        (message) => message.topic == topic,
      )?.payload
      return returnValue
    },

    getMessagesOnTopics: (state) => (topics: string[]) => {
      const returnValue = state.messages.filter((message) =>
        topics.includes(message.topic),
      )
      return returnValue
    },
  },

  actions: {
    async createConnection() {
      try {
        if (this.connecting) {
          await this.client.end()
          this.initData()
        }
        const { protocol, host, port, endpoint, ...options } = this.connection
        const connectUrl = `${protocol}://${host}:${port}${endpoint}`
        this.connecting = true
        this.client = await mqtt.connect(connectUrl, options)

        if (this.client.on) {
          this.client.on('connect', () => {
            this.connected = true
            this.connecting = false
            this.connectionError = ''

            this.doRawPublish(
              MqttTopic.WEBAPP_CONNECTED,
              JSON.stringify({ ts: Date.now() }),
              this.client,
            )
          })
          this.client.on('reconnect', this.handleOnReConnect)
          this.client.on('error', (error) => {
            console.error('Connection error', error)
          })
          this.client.on('disconnect', () => {
            this.initData()
          })
          this.client.on('end', () => {
            this.initData()
          })
          this.client.on('offline', () => {
            this.connected = false
          })
          this.client.on('message', (topic: string, message) => {
            const messageObj = JSON.parse(message.toString())
            if (messageObj !== undefined) {
              if (Number.parseInt(messageObj.value)) {
                this.updateArray(this.messages, {
                  topic: topic,
                  payload: Number.parseInt(messageObj.value),
                })
              } else {
                this.updateArray(this.messages, {
                  topic: topic,
                  payload: messageObj,
                })
              }
            }
          })
        }
      } catch (error) {
        console.error('Connect error', error)
      }
    },

    async doSubscribe(topic: string, client: AsyncMqttClient) {
      if (client.connected) {
        try {
          await client.subscribe(topic, { qos: 1 })
          return true
        } catch {
          return false
        }
      } else {
        return false
      }
    },

    async doUnSubscribe(topic: string, client: AsyncMqttClient) {
      if (client.connected) {
        try {
          await client.subscribe(topic)
          return true
        } catch {
          return false
        }
      } else {
        return false
      }
    },

    async doPublish(
      message: MqttMessage,
      definition: MqttValue,
      client: AsyncMqttClient,
    ) {
      if (client.connected) {
        try {
          let preSubmitOutput

          if (definition.preSubmitFunction) {
            preSubmitOutput = definition.preSubmitFunction(
              message,
              definition.conversionValue,
            )
          } else {
            const finalValue = mqttValueConversion(
              message.payload,
              definition.conversionValue,
              'submit',
            )
            const setValue = JSON.stringify({
              value: finalValue,
            })
            preSubmitOutput = {
              topic: `${message.topic}/set`,
              payload: setValue,
            }
          }

          await client.publish(preSubmitOutput.topic, preSubmitOutput.payload, {
            qos: 1,
          })
        } catch (error) {
          console.warn('Publish error', error)
        }
      }
    },

    async doRawPublish(
      topic: string,
      payload: string,
      client: AsyncMqttClient,
    ) {
      if (client.connected) {
        try {
          await client.publish(topic, payload, { qos: 1 })
        } catch (error) {
          console.warn('Publish error', error)
        }
      }
    },

    async destroyConnection(client: AsyncMqttClient) {
      if (client.connected) {
        try {
          await client.end(false)
          this.initData()
        } catch (error) {
          console.warn('Disconnect error', error)
        }
      }
    },

    initData() {
      this.retryTimes = 0
      this.connected = false
      this.connecting = false
    },

    async handleOnReConnect() {
      this.retryTimes += 1
      if (this.retryTimes >= 5) {
        try {
          await this.client.end()
          this.initData()
          this.connectionError = 'error'
        } catch {
          this.connectionError = 'error'
        }
      }
    },

    handleProtocolChange(value: string) {
      this.connection.port = value === 'wss' ? 8084 : 8083
    },

    getSubscriptionsFromDefinition(
      obj: MqttContainer | MqttValue,
    ): MqttSubscription[] {
      const result: MqttSubscription[] = []

      if (instanceOfMqttValue(obj)) {
        const subscription: MqttSubscription = {
          title: obj.title,
          subscribed: false,
          topic: obj.topic,
        }

        result.push(subscription)
      } else if (obj.values && obj.values.length > 0) {
        obj.values.forEach((value) => {
          const valueResult = this.getSubscriptionsFromDefinition(value)
          result.push(...valueResult)
        })
      }

      return result
    },

    getTopicsFromDefinition(obj: MqttContainer | MqttValue): string[] {
      const result: string[] = []

      if (instanceOfMqttValue(obj)) {
        result.push(obj.topic)
      } else if (obj.values && obj.values.length > 0) {
        obj.values.forEach((value) => {
          const valueResult = this.getTopicsFromDefinition(value)
          result.push(...valueResult)
        })
      }

      return result
    },

    updateArray(arr: MqttMessage[], obj: MqttMessage) {
      const foundIndex = arr.findIndex((message) => message.topic === obj.topic)
      foundIndex !== -1 ? (arr[foundIndex] = obj) : arr.push(obj)
    },

    async discoverIPAddress(macAddress: string) {
      const data = await discoverService.fetchIPAddressByMacAddress({
        macAddress: macAddress,
      })

      if (data) this.connection.host = data.InternalIp

      return data
    },
  },
})
